import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ProjectData } from "../../api/createProjectApi";

type Item = {
  id: string;
  name: string;
  [k: string]: any;
};

interface GenericMultiSelectProps {
  items: Item[];
  label?: string;
  placeholder?: string;
  width?: string;
  search?: boolean;
  showSelectAll?: boolean;
  className?: string;
  projectData: ProjectData;
  setProjectData: React.Dispatch<React.SetStateAction<ProjectData>>;
  valueToCheck: string;
}

export function GenericMultiSelect({
  items,
  label,
  placeholder = "-- Select --",
  width = "w-72",
  search = true,
  showSelectAll = true,
  className = "",
  projectData,
  setProjectData,
  valueToCheck,
}: GenericMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  useEffect(() => {
    if (mode != "edit") return;
  }, [projectData]);

  useEffect(() => {
    if (
      mode === "edit" &&
      projectData[valueToCheck] &&
      !projectData[`${valueToCheck}Ids`]?.length
    ) {
      setProjectData((prev: any) => ({
        ...prev,
        [`${valueToCheck}Ids`]: projectData[valueToCheck].map((i: any) =>
          String(i.id),
        ),
      }));
    }
  }, [mode, projectData[valueToCheck]]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Filter items based on search query
  const filtered = query
    ? items.filter(
        (it) =>
          String(it.name)?.toLowerCase()?.includes(query.toLowerCase()) ||
          String(it.id)?.toLowerCase()?.includes(query.toLowerCase()),
      )
    : items;

  // Compute dynamic selected IDs
  const idsFieldName =
    valueToCheck === "instructors"
      ? "instructorIds"
      : valueToCheck === "teachingAssistants"
        ? "teachingAssId"
        : `${valueToCheck}Ids`;

  const dynamicSelectedIds: string[] =
    projectData[idsFieldName] ??
    (mode === "edit" && projectData[valueToCheck]
      ? projectData[valueToCheck].map((i: any) => String(i.id))
      : []);

  // Toggle selection
  const toggleId = (id: string) => {
    const exists = dynamicSelectedIds.includes(id);
    const updatedIds = exists
      ? dynamicSelectedIds.filter((x) => x !== id)
      : [...dynamicSelectedIds, id];

    setProjectData((prev: any) => {
      let primaryIdsField = "";
      let backendFieldName = "";
      let altFieldName = "";

      if (valueToCheck === "instructors") {
        primaryIdsField = "instructorIds";
        backendFieldName = "instructor_id";
        altFieldName = "instructorsIds";
      } else if (valueToCheck === "teachingAssistants") {
        primaryIdsField = "teachingAssId";
        backendFieldName = "teaching_ass_id";
        altFieldName = "teachingAssistantsIds";
      }

      const newData = {
        ...prev,
        [primaryIdsField]: updatedIds,
        ...(altFieldName ? { [altFieldName]: updatedIds } : {}),
        ...(backendFieldName ? { [backendFieldName]: updatedIds } : {}),
      };
      return newData;
    });
  };

  // Select all / clear all
  const selectAll = () => {
    const allIds = items.map((it) => String(it.id));

    setProjectData((prev: any) => {
      let primaryIdsField = "";
      let backendFieldName = "";
      let altFieldName = "";

      if (valueToCheck === "instructors") {
        primaryIdsField = "instructorIds";
        backendFieldName = "instructor_id";
        altFieldName = "instructorsIds";
      } else if (valueToCheck === "teachingAssistants") {
        primaryIdsField = "teachingAssId";
        backendFieldName = "teaching_ass_id";
        altFieldName = "teachingAssistantsIds";
      }

      return {
        ...prev,
        [primaryIdsField]: allIds,
        ...(altFieldName ? { [altFieldName]: allIds } : {}),
        ...(backendFieldName ? { [backendFieldName]: allIds } : {}),
      };
    });
  };

  const clearAll = () => {
    setProjectData((prev: any) => {
      let primaryIdsField = "";
      let backendFieldName = "";
      let altFieldName = "";

      if (valueToCheck === "instructors") {
        primaryIdsField = "instructorIds";
        backendFieldName = "instructor_id";
        altFieldName = "instructorsIds";
      } else if (valueToCheck === "teachingAssistants") {
        primaryIdsField = "teachingAssId";
        backendFieldName = "teaching_ass_id";
        altFieldName = "teachingAssistantsIds";
      }

      const newData = {
        ...prev,
        [valueToCheck]: [],
        [primaryIdsField]: [],
        ...(altFieldName ? { [altFieldName]: [] } : {}),
        ...(backendFieldName ? { [backendFieldName]: [] } : {}),
      };
      return newData;
    });
  };

  // Display selected names in button
  const labelStr =
    dynamicSelectedIds.length > 0
      ? dynamicSelectedIds
          .map((id) => items.find((it) => String(it.id) === id)?.name ?? id)
          .join(", ")
      : (label ?? placeholder);

  return (
    <div className="relative inline-block text-left" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${width} text-left text-sm font-normal text-bits-grey-500 border bg-bits-grey-100 px-3 py-2 rounded flex items-center justify-between ${className}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="truncate">{labelStr}</span>
        <span className="ml-2">▾</span>
      </button>

      {open && (
        <div
          className="absolute mt-2 bg-white shadow-lg border rounded z-50 max-h-72 overflow-auto p-2"
          style={{ minWidth: 280 }}
          role="menu"
        >
          <div className="flex flex-col gap-2">
            {search && (
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="px-2 py-1 border rounded text-sm"
              />
            )}

            <div className="flex items-center justify-between gap-2">
              {showSelectAll && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-sm px-2 py-1 rounded hover:bg-gray-100"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-sm px-2 py-1 rounded hover:bg-gray-100"
                  >
                    Clear
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm px-3 py-1 rounded bg-bits-grey-100"
              >
                Done
              </button>
            </div>

            <div className="divide-y overflow-auto">
              {filtered.length === 0 ? (
                <div className="p-2 text-sm text-gray-500">No items</div>
              ) : (
                filtered.map((it) => {
                  const idStr = String(it.id);
                  const checked = dynamicSelectedIds.includes(idStr);
                  return (
                    <button
                      key={idStr}
                      type="button"
                      onClick={() => toggleId(idStr)}
                      className={`w-full flex items-center justify-between gap-2 p-2 rounded hover:bg-gray-50 text-left ${
                        checked ? "bg-bits-grey-50" : ""
                      }`}
                    >
                      <div className="text-sm truncate">
                        {it.name}{" "}
                        {it.email && (
                          <span className="text-xs text-gray-400">
                            ({it.email})
                          </span>
                        )}
                      </div>
                      {checked ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8 11.293l6.293-6.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <span className="w-4 h-4" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
